import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from '@reduxjs/toolkit';
import mineralDiscoveryReducer from './slices/mineralDiscoverySlice';
import mapReducer from './slices/mapSlice';
import reportVisualizationReducer from './slices/reportVisualizationSlice';
import drillClusterReducer from './slices/drillClusterSlice';
import { websocketMiddleware } from './middleware/websocketMiddleware';

// Configure persistence for reportVisualization slice
const reportVisualizationPersistConfig = {
  key: 'reportVisualization',
  storage,
  whitelist: [
    'selectedAnalysisId',
    'viewMode',
    'showDetailedReport',
    'settings',
    'expandedPanels',
    'activeFilters'
  ]
};

const rootReducer = combineReducers({
  mineralDiscovery: mineralDiscoveryReducer,
  map: mapReducer,
  reportVisualization: persistReducer(reportVisualizationPersistConfig, reportVisualizationReducer),
  drillClusters: drillClusterReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore redux-persist actions and existing OpenLayers/visualization data
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          'map/setMapInstance', 
          'mineralDiscovery/setProbabilityData',
          'reportVisualization/fetchVisualizationData/fulfilled',
          'reportVisualization/processTargetRanking/fulfilled'
        ],
        ignoredPaths: [
          'map.mapInstance',
          'mineralDiscovery.probabilityData.bounds',
          'reportVisualization.processedData',
          'reportVisualization.processedData.anomalyLayers',
          'reportVisualization.selectedAnalysisId.processedData.anomalyLayers',
          'reportVisualization.selectedAnalysisData.processedData.anomalyLayers',
          'reportVisualization.processedData.spatialPriority.probabilityGrid',
          'reportVisualization.processedData.spatialPriority.confidenceGrid',
          'reportVisualization.viewMode.processedData.anomalyLayers'
        ],
        // Ignore all fields matching regex pattern
        ignoredActionPaths: [
          /reportVisualization\..*\.processedData\.anomalyLayers/,
          /reportVisualization\..*\.spatialPriority/
        ],
      },
    }).concat(websocketMiddleware),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
